import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export const AMAZON_IMPORT_COLUMNS = [
  'sku',
  'product_name',
  'brand_name',
  'product_category',
  'product_description',
  'bullet_point_1',
  'bullet_point_2',
  'bullet_point_3',
  'bullet_point_4',
  'bullet_point_5',
  'search_terms',
  'model_number',
  'retail_price',
  'map_price',
  'cost_of_goods',
  'inventory_quantity',
  'upc_ean_gtin',
  'asin',
  'fulfillment_method',
  'country_of_origin',
  'product_length',
  'product_width',
  'product_height',
  'product_weight',
  'packaging_length',
  'packaging_width',
  'packaging_height',
  'packaging_weight',
  'units_per_case',
  'has_variations',
  'variation_options',
  'expiration_shelf_life',
  'hazardous_materials',
  'prep_labeling_requirements',
];

const REQUIRED_COLUMNS = ['sku', 'product_name', 'brand_name', 'product_category'];

const NUMBER_COLUMNS = [
  'retail_price',
  'map_price',
  'cost_of_goods',
  'product_length',
  'product_width',
  'product_height',
  'product_weight',
  'packaging_length',
  'packaging_width',
  'packaging_height',
  'packaging_weight',
];

const INTEGER_COLUMNS = ['inventory_quantity', 'units_per_case'];

const BOOLEAN_COLUMNS = ['has_variations', 'hazardous_materials'];

const ALLOWED_FULFILLMENT = new Set(['FBA', 'FBM']);

const HEADER_LOOKUP = AMAZON_IMPORT_COLUMNS.reduce((acc, col) => {
  acc[col.toLowerCase()] = col;
  return acc;
}, {});

const EXAMPLE_ROW = {
  sku: 'AMZ-SKU-001',
  product_name: 'Example Amazon Product',
  brand_name: 'My Brand',
  product_category: 'Health & Beauty',
  product_description: 'Full product description here',
  bullet_point_1: 'Key feature one',
  bullet_point_2: 'Key feature two',
  bullet_point_3: 'Key feature three',
  bullet_point_4: '',
  bullet_point_5: '',
  search_terms: 'keyword1 keyword2 keyword3',
  model_number: 'MODEL-001',
  retail_price: 29.99,
  map_price: 24.99,
  cost_of_goods: 10.00,
  inventory_quantity: 100,
  upc_ean_gtin: '012345678901',
  asin: '',
  fulfillment_method: 'FBA',
  country_of_origin: 'United Kingdom',
  product_length: 15,
  product_width: 8,
  product_height: 5,
  product_weight: 0.3,
  packaging_length: 16,
  packaging_width: 9,
  packaging_height: 6,
  packaging_weight: 0.35,
  units_per_case: 12,
  has_variations: 'false',
  variation_options: '',
  expiration_shelf_life: '24 months',
  hazardous_materials: 'false',
  prep_labeling_requirements: '',
};

const normalizeRawRow = (rawRow) => {
  const normalized = {};
  Object.keys(rawRow || {}).forEach((rawKey) => {
    const key = String(rawKey || '').trim().toLowerCase();
    const canonical = HEADER_LOOKUP[key];
    if (!canonical) return;
    normalized[canonical] = rawRow[rawKey];
  });
  return normalized;
};

const isRowEmpty = (row) => AMAZON_IMPORT_COLUMNS.every((col) => {
  const value = row[col];
  return value === undefined || value === null || String(value).trim() === '';
});

const emptyToNull = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
};

const parseNumber = (value, integerOnly = false) => {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  const numeric = Number(String(value).trim());
  if (!Number.isFinite(numeric)) return Number.NaN;
  if (integerOnly && !Number.isInteger(numeric)) return Number.NaN;
  return numeric;
};

const parseBoolean = (value) => {
  if (value === undefined || value === null) return null;
  const lower = String(value).trim().toLowerCase();
  if (lower === 'true' || lower === 'yes' || lower === '1') return true;
  if (lower === 'false' || lower === 'no' || lower === '0' || lower === '') return false;
  return null;
};

const validateHeaders = (rawRows) => {
  if (!rawRows.length) {
    return REQUIRED_COLUMNS.map((col) => ({
      row: 1,
      field: col,
      error: `Missing required header "${col}"`,
    }));
  }
  const normalizedHeaders = new Set();
  Object.keys(rawRows[0] || {}).forEach((header) => {
    const canonical = HEADER_LOOKUP[String(header || '').trim().toLowerCase()];
    if (canonical) normalizedHeaders.add(canonical);
  });
  return REQUIRED_COLUMNS
    .filter((col) => !normalizedHeaders.has(col))
    .map((col) => ({
      row: 1,
      field: col,
      error: `Missing required header "${col}"`,
    }));
};

const validateAndNormalizeRows = (rawRows) => {
  const errors = [];
  const validRows = [];
  const seenSkus = new Set();

  rawRows.forEach((rawRow, index) => {
    const rowNumber = index + 2;
    const row = normalizeRawRow(rawRow);
    if (isRowEmpty(row)) return;

    const normalized = {};
    AMAZON_IMPORT_COLUMNS.forEach((col) => {
      normalized[col] = emptyToNull(row[col]);
    });

    REQUIRED_COLUMNS.forEach((col) => {
      if (!normalized[col]) {
        errors.push({ row: rowNumber, field: col, error: `${col} is required` });
      }
    });

    NUMBER_COLUMNS.forEach((col) => {
      const parsed = parseNumber(normalized[col], false);
      if (Number.isNaN(parsed)) {
        errors.push({ row: rowNumber, field: col, error: `${col} must be a valid number` });
      } else {
        normalized[col] = parsed;
      }
    });

    INTEGER_COLUMNS.forEach((col) => {
      const parsed = parseNumber(normalized[col], true);
      if (Number.isNaN(parsed)) {
        errors.push({ row: rowNumber, field: col, error: `${col} must be a whole number` });
      } else {
        normalized[col] = parsed;
      }
    });

    BOOLEAN_COLUMNS.forEach((col) => {
      if (normalized[col] !== null) {
        const parsed = parseBoolean(normalized[col]);
        if (parsed === null) {
          errors.push({ row: rowNumber, field: col, error: `${col} must be true or false` });
        } else {
          normalized[col] = parsed;
        }
      }
    });

    if (normalized.fulfillment_method && !ALLOWED_FULFILLMENT.has(normalized.fulfillment_method.toUpperCase())) {
      errors.push({ row: rowNumber, field: 'fulfillment_method', error: 'fulfillment_method must be FBA or FBM' });
    } else if (normalized.fulfillment_method) {
      normalized.fulfillment_method = normalized.fulfillment_method.toUpperCase();
    }

    const skuKey = (normalized.sku || '').toLowerCase();
    if (skuKey) {
      if (seenSkus.has(skuKey)) {
        errors.push({ row: rowNumber, field: 'sku', error: 'Duplicate SKU in import file' });
      } else {
        seenSkus.add(skuKey);
      }
    }

    const hasRowError = errors.some((e) => e.row === rowNumber);
    if (!hasRowError) {
      validRows.push({ ...normalized, __rowNumber: rowNumber });
    }
  });

  return { validRows, errors };
};

const parseCsv = async (file) => {
  const text = await file.text();
  const { data, errors } = Papa.parse(text, { header: true, skipEmptyLines: 'greedy' });
  const parseErrors = (errors || []).map((e) => ({
    row: (e.row || 0) + 1,
    field: 'file',
    error: e.message || 'Unable to parse CSV row',
  }));
  return { rows: data || [], parseErrors };
};

const parseXlsx = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const names = workbook.SheetNames || [];
  const preferred = names.find((n) => String(n).trim().toLowerCase() === 'amazon products');
  const sheetName = preferred || names[0];
  if (!sheetName) return { rows: [], parseErrors: [] };
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return { rows, parseErrors: [] };
};

export const parseAndValidateAmazonImportFile = async (file) => {
  const fileName = String(file?.name || '').toLowerCase();
  let parsed;
  if (fileName.endsWith('.csv')) {
    parsed = await parseCsv(file);
  } else if (fileName.endsWith('.xlsx')) {
    parsed = await parseXlsx(file);
  } else {
    return {
      validRows: [],
      errors: [{ row: 0, field: 'file', error: 'Unsupported file type. Please upload CSV or XLSX.' }],
    };
  }

  const headerErrors = validateHeaders(parsed.rows);
  if (headerErrors.length > 0) {
    return { validRows: [], errors: [...headerErrors, ...parsed.parseErrors] };
  }

  const validated = validateAndNormalizeRows(parsed.rows);
  return {
    validRows: validated.validRows,
    errors: [...parsed.parseErrors, ...validated.errors],
  };
};

const triggerDownload = (fileName, blob) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadAmazonTemplateCsv = () => {
  const csv = Papa.unparse([EXAMPLE_ROW], { columns: AMAZON_IMPORT_COLUMNS });
  triggerDownload('amazon-products-template.csv', new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
};

export const downloadAmazonTemplateXlsx = () => {
  const worksheet = XLSX.utils.json_to_sheet([EXAMPLE_ROW], { header: AMAZON_IMPORT_COLUMNS });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Amazon Products');
  XLSX.writeFile(workbook, 'amazon-products-template.xlsx');
};
